import { VirtualizedListbox, VirtualizedPopper } from '@components/VirtualizedListbox/VirtualizedListbox'
import { PopperProps } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { OutOfStockStatusEnum } from '../../../../generated/gqlTypes'

export interface VariantOption {
  pk: number
  label: string
  attributeName: string
  isSupply: boolean
  shippingRequired: boolean
  stockStatus: OutOfStockStatusEnum
}

// Typing to filter only helps on long lists; short ones behave like a plain select.
const SEARCHABLE_FROM = 10

// The card can be narrower than the option labels.
const OptionsPopper: React.FC<PopperProps> = props => (
  <VirtualizedPopper {...props} placement='bottom-start' style={{ ...props.style, minWidth: 240 }} />
)

const OptionsPaper: React.FC = props => <Paper {...props} elevation={4} />

interface Props {
  options: VariantOption[]
  selectedPk: number | null
  // Shows the selection but doesn't open (the only option is preselected). Unlike `disabled`, it isn't greyed out.
  readOnly?: boolean

  onChange(pk: number): void
}

// What the options choose, for the prompts: "Designs ##" -> "designs"; null for several attributes (a generic prompt).
export const getOptionsName = (options: VariantOption[]) => {
  const attributeNames = new Set(options.map(option => option.attributeName))
  return attributeNames.size === 1 ? [...attributeNames][0].replace(/[#\s]+$/, '').toLowerCase() : null
}

// Variant picker of the card panel. Options are rendered with react-window: a product can have 5000+ variants.
const VariantSelect: React.FC<Props> = ({ options, selectedPk, readOnly = false, onChange }) => {
  const { t } = useTranslation()
  const selected = options.find(option => option.pk === selectedPk) || null
  const name = getOptionsName(options)
  const placeholder = name
    ? t('productCard.chooseAttribute', '— Choose {{name}} —', { name })
    : t('productCard.chooseOption', '— Choose option —')

  const statusLabel = (option: VariantOption) => {
    if (option.stockStatus === OutOfStockStatusEnum.Out) return t('productOutOfStock', 'Out of Stock')
    if (option.stockStatus === OutOfStockStatusEnum.Backorder) return t('productBackorder', 'Backorder')
    return null
  }

  return (
    <Autocomplete
      className='product-card-panel__select'
      options={options}
      value={selected}
      onChange={(event, option: VariantOption | null) => option && onChange(option.pk)}
      getOptionLabel={option => option.label}
      getOptionDisabled={option => option.stockStatus === OutOfStockStatusEnum.Out}
      isOptionEqualToValue={(option, value) => option.pk === value.pk}
      renderOption={({ key, ...props }: React.HTMLAttributes<HTMLLIElement> & { key: string }, option) => {
        const status = statusLabel(option)
        return (
          <li {...props} key={option.pk}>
            {status ? `${option.label} (${status})` : option.label}
          </li>
        )
      }}
      disableClearable={!!selected}
      disableListWrap
      openOnFocus
      readOnly={readOnly}
      // Without the arrow the locked field doesn't look like it can be opened.
      forcePopupIcon={!readOnly}
      ListboxComponent={VirtualizedListbox}
      PopperComponent={OptionsPopper}
      PaperComponent={OptionsPaper}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={placeholder}
          size='small'
          // The theme's default margin would make the field taller than the 40px row of the KIT/PDF buttons and the
          // skeleton, so the panel would jump when the data arrives.
          margin='none'
          sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
          error={!selected}
          inputProps={{
            ...params.inputProps,
            readOnly: options.length < SEARCHABLE_FROM,
            'aria-label': placeholder,
            className: `browser-default data-hj-allow ${params.inputProps.className || ''}`,
          }}
        />
      )}
    />
  )
}

export default VariantSelect
